import {
  GOODS_RECEIPT_DEVIATION_LABELS,
  GOODS_RECEIPT_STATUS_LABELS,
  type ExpectedDelivery,
  type GoodsReceiptDeviation,
  type GoodsReceiptItemResponse,
  type GoodsReceiptStatus,
} from '@proerp/shared';

type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

/** Badge-Ton je Status. Die Labels kommen aus @proerp/shared (Single Source of Truth). */
export const GOODS_RECEIPT_STATUS_TONE: Record<GoodsReceiptStatus, Tone> = {
  OFFEN: 'warning',
  GEPRUEFT: 'info',
  GEBUCHT: 'success',
  STORNIERT: 'neutral',
};

/**
 * Bestellnummer zur Anzeige — mit „(gelöscht)", wenn die Bestellung inzwischen
 * gelöscht ist (Altfall vor dem Löschschutz, Befund 2026-09-28).
 */
export function purchaseOrderLabel(receipt: { purchaseOrderNumber: string; purchaseOrderDeleted?: boolean }): string {
  return receipt.purchaseOrderDeleted ? `${receipt.purchaseOrderNumber} (gelöscht)` : receipt.purchaseOrderNumber;
}

export function goodsReceiptStatusLabel(status: GoodsReceiptStatus): string {
  return GOODS_RECEIPT_STATUS_LABELS[status];
}

/** Badge-Ton je Abweichung — „Vollständig" bleibt unauffällig, Schaden ist rot. */
export const DEVIATION_TONE: Record<GoodsReceiptDeviation, Tone> = {
  KEINE: 'success',
  UNTERLIEFERUNG: 'warning',
  UEBERLIEFERUNG: 'info',
  BESCHAEDIGT: 'danger',
};

export function deviationLabel(deviation: GoodsReceiptDeviation): string {
  return GOODS_RECEIPT_DEVIATION_LABELS[deviation];
}

/**
 * Ablauf als Stufenleiste (Muster TransportStatusStepper). `STORNIERT` ist kein
 * Schritt, sondern ein Abbruch — dafür ein eigener Hinweis.
 */
export const GOODS_RECEIPT_FLOW: GoodsReceiptStatus[] = ['OFFEN', 'GEPRUEFT', 'GEBUCHT'];

export function goodsReceiptFlowIndex(status: GoodsReceiptStatus): number {
  const index = GOODS_RECEIPT_FLOW.indexOf(status);
  return index === -1 ? 0 : index;
}

/** Kurzform der Stufen in der Leiste — „Prüfung läuft" ist als Stufenname zu lang. */
export const GOODS_RECEIPT_FLOW_LABELS: Record<GoodsReceiptStatus, string> = {
  OFFEN: 'Prüfung',
  GEPRUEFT: 'Geprüft',
  GEBUCHT: 'Gebucht',
  STORNIERT: 'Storniert',
};

/**
 * Was fehlt an dieser Position noch, damit die Prüfung abgeschlossen werden kann?
 * Spiegelt die Server-Regel (`complete`): Foto bei jeder Abweichung, Bemerkung bei
 * Überlieferung und Schaden. Die UI zeigt es VORHER, damit niemand erst in den
 * Fehler läuft — durchgesetzt wird es trotzdem serverseitig.
 */
export function missingProofs(
  item: Pick<GoodsReceiptItemResponse, 'deviation' | 'note' | 'photos'>,
  requiresPhoto: readonly GoodsReceiptDeviation[],
  requiresNote: readonly GoodsReceiptDeviation[],
): string[] {
  const missing: string[] = [];
  if (requiresPhoto.includes(item.deviation) && item.photos.length === 0) missing.push('Foto');
  if (requiresNote.includes(item.deviation) && !item.note?.trim()) missing.push('Bemerkung');
  return missing;
}

// ── Erwartete Lieferungen ───────────────────────────────────────────────────

/** Beginn des heutigen Tages als Zeitstempel — Basis jedes Termin-Vergleichs. */
export function startOfToday(now: Date = new Date()): number {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return today.getTime();
}

/**
 * Wie viele Tage ist der Liefertermin überfällig? 0 = heute oder später, `null` =
 * kein Termin vereinbart. Ohne Termin ist nichts überfällig — eine Bestellung ohne
 * zugesagtes Datum zu mahnen wäre falsch.
 */
export function daysOverdue(expectedDate: string | null, todayStart: number): number | null {
  if (!expectedDate) return null;
  const expected = new Date(expectedDate);
  if (Number.isNaN(expected.getTime())) return null;
  expected.setHours(0, 0, 0, 0);
  const diff = todayStart - expected.getTime();
  if (diff <= 0) return 0;
  return Math.round(diff / 86_400_000);
}

export function isDeliveryOverdue(delivery: ExpectedDelivery, todayStart: number): boolean {
  return (daysOverdue(delivery.expectedDate, todayStart) ?? 0) > 0;
}

/**
 * Reihenfolge an der Rampe: der früheste zugesagte Termin zuerst, damit das
 * Überfällige oben steht. Bestellungen ohne Termin ans Ende — sie sind nicht
 * dringend, sie sind unbestimmt; zwischen den datierten würden sie nur stören.
 * Gleichstand nach Lieferant, damit die Reihenfolge zwischen zwei Aufrufen stabil
 * bleibt (sonst springt die Liste beim Neuladen).
 */
export function compareExpectedDeliveries(a: ExpectedDelivery, b: ExpectedDelivery): number {
  if (Boolean(a.expectedDate) !== Boolean(b.expectedDate)) return a.expectedDate ? -1 : 1;
  if (a.expectedDate && b.expectedDate && a.expectedDate !== b.expectedDate) {
    return a.expectedDate < b.expectedDate ? -1 : 1;
  }
  const bySupplier = a.supplierName.localeCompare(b.supplierName, 'de');
  return bySupplier !== 0 ? bySupplier : a.number.localeCompare(b.number, 'de', { numeric: true });
}

/** Termin-Text für die Liste: „überfällig seit 3 Tagen" schlägt ein nacktes Datum. */
export function expectedDateHint(expectedDate: string | null, todayStart: number): string | null {
  const overdue = daysOverdue(expectedDate, todayStart);
  if (overdue === null || overdue === 0) return null;
  return overdue === 1 ? 'überfällig seit gestern' : `überfällig seit ${overdue} Tagen`;
}
